export type AccountType = "player" | "venue" | "admin";

export type SignupAccountType = Extract<AccountType, "player" | "venue">;

export type AdminRole =
  | "owner"
  | "operations"
  | "support"
  | "finance"
  | "moderation"
  | "growth"
  | "readonly";

export type AdminModuleKey =
  | "overview"
  | "users"
  | "venues"
  | "bookings"
  | "finance"
  | "plans"
  | "arena"
  | "growth"
  | "privacy"
  | "support"
  | "audit"
  | "settings";

const ADMIN_ROLES = new Set<AdminRole>([
  "owner",
  "operations",
  "support",
  "finance",
  "moderation",
  "growth",
  "readonly",
]);

export const adminRoleLabels: Record<AdminRole, string> = {
  owner: "Admin Match",
  operations: "Operacao",
  support: "Suporte",
  finance: "Financeiro",
  moderation: "Moderacao",
  growth: "Growth",
  readonly: "Leitura",
};

export const adminRoleDescriptions: Record<AdminRole, string> = {
  owner: "Acesso total para acompanhar operacao, caixa, suporte e ajustes criticos.",
  operations: "Acompanha estabelecimentos, reservas, agenda, publicacao e incidentes operacionais.",
  support: "Atende jogadores e estabelecimentos com leitura operacional e acoes assistidas.",
  finance: "Controla pagamentos, assinaturas, conciliacao, repasses e riscos financeiros.",
  moderation: "Revisa denuncias, conteudo, bloqueios e confianca do marketplace.",
  growth: "Acompanha campanhas, comunicacao, planos e crescimento comercial.",
  readonly: "Acesso somente leitura para historico, registros e acompanhamento executivo.",
};

const adminRoleCapabilities: Record<AdminRole, AdminModuleKey[]> = {
  owner: ["overview", "users", "venues", "bookings", "finance", "plans", "arena", "growth", "privacy", "support", "audit", "settings"],
  operations: ["overview", "venues", "bookings", "arena", "support", "audit"],
  support: ["overview", "users", "venues", "bookings", "privacy", "support"],
  finance: ["overview", "finance", "plans", "bookings", "audit"],
  moderation: ["overview", "users", "venues", "support", "audit"],
  growth: ["overview", "users", "venues", "plans", "arena", "growth", "support"],
  readonly: ["overview", "users", "venues", "bookings", "finance", "plans", "arena", "growth", "privacy", "support", "audit"],
};

function readMetadataValue(metadata: unknown, key: string) {
  if (!metadata || typeof metadata !== "object") return undefined;
  return (metadata as Record<string, unknown>)[key];
}

export function isPlatformAdminRole(roles: string[]) {
  return roles.includes("platform_admin");
}

export function resolveAccountType(
  metadata: unknown,
  roles: string[],
  tenantRoles: Array<{ tenant_id: string; role: string }> = [],
): AccountType {
  if (isPlatformAdminRole(roles)) {
    return "admin";
  }

  if (roles.includes("venue_owner") || roles.includes("venue_manager") || tenantRoles.length > 0) {
    return "venue";
  }

  // user_metadata is user-controlled in Supabase. Keep it as display/onboarding
  // context only; it must never promote access after signup.
  return "player";
}

export function resolveAdminRole(metadata: unknown, roles: string[]): AdminRole | null {
  if (!isPlatformAdminRole(roles)) {
    return null;
  }

  const metadataRole = readMetadataValue(metadata, "admin_role");
  if (typeof metadataRole === "string" && ADMIN_ROLES.has(metadataRole as AdminRole)) {
    return metadataRole as AdminRole;
  }

  return "owner";
}

export function canAccessAdminModule(adminRole: AdminRole | null, module: AdminModuleKey) {
  if (!adminRole) return false;
  return adminRoleCapabilities[adminRole].includes(module);
}
